import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Box,
  Typography
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "../../utils/toast";

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  strategyGroup: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : "#f5f5f5",
    borderRadius: 4
  },
  conflictRow: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 167, 38, 0.1)" : "#fff3e0"
  },
  radioGroup: {
    marginLeft: theme.spacing(2)
  }
}));

const ImportContactsModal = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [strategies, setStrategies] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      setPreview(null);
      setResults(null);
      setStrategies({});
      setError(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/contacts/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPreview(response.data);
      setStrategies({});
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao processar arquivo");
    } finally {
      setLoading(false);
    }
  };

  const handleStrategyChange = (conflictId, strategy) => {
    setStrategies(prev => ({
      ...prev,
      [conflictId]: strategy
    }));
  };

  const handleApplyImport = async () => {
    if (!preview) return;

    setLoading(true);
    setError(null);

    try {
      const conflictsWithData = preview.conflicts.map((conflict, idx) => ({
        contactId: conflict.existingContact.id,
        newContact: conflict.newContact,
        resolution: {
          contactId: conflict.existingContact.id,
          strategy: strategies[idx] || "keep-name"
        }
      }));

      const response = await api.post("/contacts/import/apply", {
        newContacts: preview.newContacts,
        conflicts: conflictsWithData
      });

      const { created, updated, skipped, failed } = response.data;
      
      setPreview(null);
      setStrategies({});
      if (fileInputRef.current) fileInputRef.current.value = "";
      
      const failedCount = failed?.length || 0;
      
      if (failedCount === 0) {
        toast.success("Importação concluída com sucesso!");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setResults({ created, updated, skipped, failed });
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Erro ao importar contatos";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {results ? "Resultados da Importação" : preview ? "Resolver conflitos de importação" : "Importar Contatos"}
      </DialogTitle>

      <DialogContent className={classes.container}>
        {error && <Alert severity="error">{error}</Alert>}

        {!preview && !results && (
          <Box>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              Selecione um arquivo Excel para importar contatos
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
              disabled={loading}
              style={{ marginBottom: 16 }}
            />
            {loading && <CircularProgress />}
          </Box>
        )}

        {preview && !results && (
          <Box>
            <Typography variant="h6">
              Total de linhas: {preview.totalRows}
            </Typography>
            <Typography variant="body2">
              Novos contatos: {preview.newContacts.length} | Conflitos: {preview.conflicts.length}
            </Typography>

            {preview.conflicts.length > 0 && (
              <Box style={{ marginTop: 24 }}>
                <Typography variant="h6">Conflitos encontrados:</Typography>
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                  Escolha como resolver cada contato duplicado:
                </Typography>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Linha</TableCell>
                        <TableCell>Novo Nome</TableCell>
                        <TableCell>Nome Existente</TableCell>
                        <TableCell>Estratégia</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.conflicts.map((conflict, idx) => (
                        <TableRow key={idx} className={classes.conflictRow}>
                          <TableCell>{conflict.rowIndex}</TableCell>
                          <TableCell>{conflict.newContact.name}</TableCell>
                          <TableCell>{conflict.existingContact.name}</TableCell>
                          <TableCell>
                            <RadioGroup
                              row
                              value={strategies[idx] || "keep-name"}
                              onChange={(e) =>
                                handleStrategyChange(idx, e.target.value)
                              }
                              className={classes.radioGroup}
                            >
                              <FormControlLabel
                                value="keep-name"
                                control={<Radio size="small" />}
                                label="Manter"
                              />
                              <FormControlLabel
                                value="update-name"
                                control={<Radio size="small" />}
                                label="Atualizar"
                              />
                              <FormControlLabel
                                value="skip"
                                control={<Radio size="small" />}
                                label="Ignorar"
                              />
                            </RadioGroup>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {results && (
          <Box>
            <Typography variant="h6" color="primary" style={{ marginBottom: 8 }}>
              Resumo da Operação
            </Typography>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              <strong>Criados:</strong> {results.created?.length || 0} <br />
              <strong>Atualizados:</strong> {results.updated?.length || 0} <br />
              <strong>Ignorados:</strong> {results.skipped?.length || 0} <br />
              <strong style={{ color: '#f44336' }}>Falhas: {results.failed?.length || 0}</strong>
            </Typography>

            {results.failed?.length > 0 && (
              <Box style={{ marginTop: 24 }}>
                <Typography variant="h6" color="error" style={{ marginBottom: 8 }}>
                  Contatos não importados (Falhas)
                </Typography>
                <TableContainer component={Paper} style={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Contato</TableCell>
                        <TableCell>Motivo da Falha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.failed.map((failStr, idx) => {
                        const parts = failStr.split(": ");
                        const contactInfo = parts[0];
                        let reason = parts[1] || "Erro desconhecido";
                        if (reason.includes("ERR_CHECK_NUMBER")) {
                          reason = "Número inválido ou não possui WhatsApp";
                        }
                        return (
                          <TableRow key={idx}>
                            <TableCell>{contactInfo}</TableCell>
                            <TableCell style={{ color: '#f44336' }}>{reason}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!results ? (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            {preview && (
              <Button
                onClick={handleApplyImport}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Importar"}
              </Button>
            )}
          </>
        ) : (
          <Button
            onClick={() => {
              setResults(null);
              onClose();
              if (onSuccess) onSuccess();
            }}
            variant="contained"
            color="primary"
          >
            Concluir e Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportContactsModal;
